<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span>{{title}}</span>
      <v-spacer></v-spacer>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <LoadableComponent
        :loading="isLoading"
        :height="400"
        >
        <FormWrapper
        :buttonText="$t('saveChanges')"
        @submit="saveChangesPressed"
        :disabled="saveBtnDisabled"
        >
          <template v-slot:right-action>
            <v-checkbox
              v-if="!isEditing"
              color="primary"
              v-model="createContractOnSave"
              :label="$t('createContractOnSave')"
              class="mr-2"
            />
          </template>
          <v-container fluid>
            <v-row dense>
              <v-col>
                <AccountAutocomplete
                :accountId="tractDestination.destinationAccountId"
                :title="$t('destinationAccount')"
                :disabled="isEditing"
                :prohibitedSet="destinationAccountsIdsSet"
                clearable
                showCertified
                userSetting="tractDestinationAutocomplete"
                @account-chosen="destinationAccountChosen"
                :fetchTagsAndAccounts="false"/>
              </v-col>
              <v-col cols="auto">
                <v-text-field
                data-testid="tract-destination-distance"
                v-model="tractDestination.distance"
                :label="$t('distance')"
                color="black"
                :rules="[rules.integer, rules.validDistance]"
              ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="10">
                <!-- TODO: IH - show byproducts in tract destination setup with TSBP -->
                <ProductSelector
                v-if="productSelectorRerenderTrigger"
                :includeByproducts="false/**includeByproducts || tractDestination.products.some(p => p.isByproduct)**/"
                :propProducts="tractDestination.products ?? []"
                :cruiseProductIds="cruiseProductIds"
                @products-selected="productsSelected"/>
              </v-col>
              <v-col cols="2">
                <v-checkbox
                  v-if="showScaleTicketsCheckbox"
                  id="tract-destination-scales-tickets"
                  v-model="scalesTickets"
                  color="primary"
                  :label="$t('scaleTickets')"
                />
              </v-col>

              <v-col cols="10" v-if="scalesTickets" class="pa-0 ma-0">
                <v-card flat class="pa-0 ma-0">
                  <v-card-title class="subtitle-1 px-0">
                    <span>{{ $t('scaleSampleFrequency') }}</span>
                    <Icon
                      icon="mdi-information-outline"
                      small
                      class="ml-2"
                      iconColor="grey"
                      :tooltipText="$t('scaleSampleFrequencyDescription')"
                    />
                    <Icon
                      v-if="showIncompatibleProductWarning"
                      icon="mdi-alert"
                      small
                      class="ml-1"
                      iconColor="warning"
                      :tooltipText="$t('incompatibleScalingProducts')"
                    />
                  </v-card-title>
                  <v-card-text class="px-0">
                    <v-row dense>
                      <v-col
                        v-for="product in eligibleScalingProducts"
                        :key="product.productId"
                        cols="12" sm="6" md="4" lg="3"
                      >
                        <v-card outlined class="pa-3">
                          <div class="mb-2">{{ product.name }}</div>
                          <v-text-field
                            v-model="product.scaleFrequency"
                            :rules="[rules.positiveIntegerBetweenOrBlank(0, 100)]"
                            dense
                            hide-details="auto"
                            color="primary"
                            type="number"
                            hide-spin-buttons
                          >
                            <template #prepend-inner>
                              <span class="grey--text body-2 mt-1">1:</span>
                            </template>
                          </v-text-field>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="10" v-if="scalesTickets" class="pa-0 ma-0">
                <v-card flat class="pa-0 ma-0" v-if="yardInstructions.length > 0">
                  <v-card-title class="subtitle-1 px-0">
                    <span>{{ $t('yardInstructionForDestination') }}</span>
                  </v-card-title>
                  <v-card-text class="px-0">
                    <v-select
                      v-model="tractDestination.yardInstructionId"
                      :items="yardInstructions"
                      :label="$t('yardInstruction')"
                      item-text="name"
                      item-value="yardInstructionId"
                      dense
                      clearable
                      hide-details="auto"
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col cols="auto">
                    <v-checkbox
                      v-model="tractDestination.requiresExt2"
                      color="primary"
                      :label="$t('requiresExt2')"
                      @change="ext2Changed"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </FormWrapper>
      </LoadableComponent>
    </v-card-text>
    <Dialog
      :stateId="dialogId"
      hideRefresh>
      <ConfirmDialog
        :title="$t('confirmDistanceUpdate')"
        :body="$t('thisWillUpdateDistanceOnContracts')"
        :confirmText="$t('confirm')"
        @cancel="closeDialog"
        @confirm="saveChanges"
      />
    </Dialog>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { mapActions, mapGetters } from 'vuex'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { TractTypeCategory, YardInstructionStatus } from '@/utils/Enumerations'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { cloneDeep } from 'lodash'

export default {
  name: 'TractDestinationForm',

  components: {
    ProductSelector: () => import('@/components/tract/tract-form/ProductSelector.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propTractDestination: {
      type: Object
    },
    tract: {
      type: Object,
      required: true
    },
    allTractDestinations: {
      type: Array
    }
  },

  data: () => ({
    rules: fieldRules.rules,
    selectedTags: [],
    tags: [],
    accounts: [],
    showMenu: false,
    scalesTickets: false,
    selectedAccount: null,
    isLoading: true,
    products: [],
    accountProductIds: [],
    fallbackProductId: null,
    fallbackProductExpands: false,
    createContractOnSave: (localStorage.getItem(LocalStorageKeys.TRACT_DESTINATION_TO_CONTRACT))
      ? localStorage.getItem(LocalStorageKeys.TRACT_DESTINATION_TO_CONTRACT) === 'true'
      : false,
    tractDestination: {
      requiresExt2: false,
      distance: 0,
      products: [],
      yardInstructionId: null
    },
    dialogId: uniqueDialogId('tract-destination-confirm-dialog'),
    accountsById: new Map(),
    productSelectorRerenderTrigger: true,
    yardInstructions: []
  }),

  computed: {
    ...mapGetters('product', ['allProducts']),
    ...mapGetters('locations', ['allLocationsByAccountId']),

    isEditing () {
      return !(this.propTractDestination === null || this.propTractDestination === undefined)
    },

    title () {
      return this.isEditing ? this.propTractDestination.destinationAccountName : this.$t('newTractDestination')
    },

    saveBtnDisabled () {
      if (this.selectedAccount === null) return true
      if (this.tractDestination.distance === '') return true
      const castedDistance = Number(this.tractDestination.distance)
      if (isNaN(castedDistance) || castedDistance < 0 || castedDistance > 250) return true
      if (!(this.tractDestination.products.length > 0)) return true

      return false
    },

    cruiseProductIds () {
      if (this.tract.type.category === TractTypeCategory.Delivered.value) {
        return null
      } else {
        const scalableProducts = this.allProducts.filter(p => p.isScalable && (this.accountProductIds.includes(p.productId) || (p.productId === this.fallbackProductId))).map(p => p.productId)
        return this.tract.tractContents.map(tc => tc.productId).concat(scalableProducts)
      }
    },

    includeByproducts () {
      return this.selectedAccount !== null &&
        !this.selectedAccount.isExternal &&
        this.tract.type.category === TractTypeCategory.Delivered.value
    },

    destinationAccountsIdsSet () {
      const accountIds = new Set()
      this.allTractDestinations.forEach(tractD => {
        accountIds.add(tractD.destinationAccountId)
      })
      return accountIds
    },

    showScaleTicketsCheckbox () {
      return this.selectedAccount && !this.selectedAccount.isExternal && this.eligibleScalingProducts.length > 0
    },

    eligibleScalingProducts () {
      return this.tractDestination.products.filter(p => p.isScalable &&
        ((p.productId !== this.fallbackProductId || (this.fallbackProductExpands && p.productId === this.fallbackProductId)) ||
        this.accountProductIds.includes(p.productId)))
    },

    showIncompatibleProductWarning () {
      if (!this.scalesTickets) {
        return false
      }

      return this.tractDestination.products.length !== this.eligibleScalingProducts.length
    }
  },

  watch: {
    showMenu (val) {
      if (!val) {
        this.getAccounts(false)
      }
    },

    createContractOnSave (create) {
      localStorage.setItem(LocalStorageKeys.TRACT_DESTINATION_TO_CONTRACT, `${create}`)
    }
  },

  async created () {
    if (this.propTractDestination) {
      this.isLoading = true
      this.tractDestination = cloneDeep(this.propTractDestination)
      this.tractDestination.products = this.tractDestination.products.map(p => {
        const serverFreq = parseFloat(p.scaleFrequency)
        const displayFreq = serverFreq && serverFreq > 0 ? Math.round(100 / serverFreq) : 0
        return {
          ...p,
          scaleFrequency: displayFreq
        }
      })
      this.tractDestination.yardInstructionId = this.propTractDestination.yardInstructionId
      this.scalesTickets = this.tractDestination.products.some(p => p.scaleFrequency > 0)
    } else {
      this.tractDestination.requiresExt2 = this.tract?.requiresExt2 ?? false
    }

    await this.getLocations()

    this.isLoading = false
  },

  methods: {
    ...mapActions('account', ['fetchAccountById']),
    ...mapActions('tract-destination', ['postTractDestination', 'updateTractDestination']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('locations', ['fetchLocations']),
    ...mapActions('yard-instructions', ['fetchYardInstructions']),

    async getAccountScalingProductInfo (accountId) {
      let account = this.accountsById.get(accountId)
      if (!account) {
        account = await this.fetchAccountById(accountId)
        this.accountsById.set(account.accountId, account)
      }
      if (account.accountProducts) {
        this.accountProductIds = account.accountProducts
          .filter(p => p.isScalable)
          .map(p => p.productId)
      } else {
        this.accountProductIds = (account.scalingProducts ?? []).map(p => p.productId)
      }
      this.fallbackProductExpands = account.fallbackExpands
      this.fallbackProductId = account.fallbackProductId
      this.productSelectorRerenderTrigger = false
      this.$nextTick(() => {
        this.productSelectorRerenderTrigger = true
      })
    },

    productsSelected (products) {
      this.tractDestination.products = products.map(product => {
        return {
          ...product,
          scaleFrequency: this.tractDestination.products.find(p => p.productId === product.productId)?.scaleFrequency || 1
        }
      })
    },

    async destinationAccountChosen (account) {
      this.selectedAccount = account
      await this.getLocations()
      if (this.selectedAccount) {
        this.getAccountScalingProductInfo(this.selectedAccount.accountId)
        if (!this.selectedAccount.isExternal) {
          this.updateYardInstructions(this.allLocationsByAccountId[this.selectedAccount.accountId].locationId)
        }
      } else {
        this.accountProductIds = []
      }
    },

    close () {
      this.$emit('close')
    },

    saveChangesPressed () {
      if (this.propTractDestination &&
        Number(this.propTractDestination.distance) !== Number(this.tractDestination.distance) &&
        this.propTractDestination.contractCount > 0) {
        this.openOrUpdateDialog({ id: this.dialogId, width: '400px' })
      } else {
        this.saveChanges()
      }
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async saveChanges () {
      const eligibleScalingProductIds = this.eligibleScalingProducts.map(p => p.productId)

      if (this.tractDestination.products.some(p => p.scaleFrequency > 0 && (!Number.isInteger(Number(p.scaleFrequency)) || p.scaleFrequency < 0 || p.scaleFrequency > 100))) {
        this.setSnackError(this.$t('invalidScaleFrequencyError'))
        return
      }

      const requestObj = {
        tractDestinationId: this.tractDestination.tractDestinationId,
        destinationAccountId: this.selectedAccount.accountId,
        distance: Number(this.tractDestination.distance),
        requiresExt2: this.tractDestination.requiresExt2,
        productIds: this.tractDestination.products.map(p => p.productId),
        tractId: this.tract.tractId,
        yardInstructionId: this.tractDestination.yardInstructionId,
        products: this.tractDestination.products.map(p => ({
          productId: p.productId,
          scaleFrequency: p.scaleFrequency && eligibleScalingProductIds.includes(p.productId) && this.scalesTickets && parseFloat(p.scaleFrequency) > 0
            ? Number((100 / parseFloat(p.scaleFrequency)).toFixed(2))
            : null
        }))
      }

      const targetTD = (this.isEditing)
        ? await this.updateTractDestination(requestObj)
        : await this.postTractDestination(requestObj)

      if (!this.isEditing && this.createContractOnSave) {
        this.$emit('create-contract', targetTD)
      } else {
        this.$emit('close')
      }
    },

    ext2Changed (requiresExt2) {
      if (requiresExt2 === true || this.tract.ticketBooks.length === 0) return
      this.setSnackError(this.$t('ext2RequiredWithTicketBooks'))
      this.$nextTick(_ => {
        this.tractDestination.requiresExt2 = true
      })
    },

    async getLocations () {
      if (!this.selectedAccount) return

      if (Object.keys(this.allLocationsByAccountId).length === 0 || !this.allLocationsByAccountId[this.selectedAccount.accountId] || this.allLocationsByAccountId[this.selectedAccount.accountId].yardInstructions.length === 0) {
        await this.fetchLocations({ includeDeckHistory: false, includeLogDecks: false, includeByproductDecks: false, includeDeckLoadCounts: false, includeProductIds: false })
      }
    },

    async updateYardInstructions (locationId) {
      if (!locationId) {
        this.yardInstructions = []
        return
      }
      const yardInstructions = await this.fetchYardInstructions(locationId)
      this.yardInstructions = yardInstructions.filter(yi => yi.status === YardInstructionStatus.Active.value)
    }
  }
}
</script>
